import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Camera, Award, ChevronRight } from 'lucide-react';

import { api, type Paginated } from '@/lib/api';
import { fmtInt } from '@/lib/format';
import type { TastingNote } from './tastingOptions';

function useAllTastings() {
  return useQuery({
    queryKey: ['tasting-notes', 'all'],
    queryFn: async () => {
      const { data } = await api.get<Paginated<TastingNote>>('/tasting-notes', {
        params: { per_page: 100 },
      });
      return data.data;
    },
  });
}

export function TastingsListPage() {
  const { data: notes, isLoading } = useAllTastings();

  // Agrupar por mes (tasted_on).
  const groups: Record<string, TastingNote[]> = {};
  for (const n of notes ?? []) {
    const m = n.tasted_on.slice(0, 7); // YYYY-MM
    (groups[m] ??= []).push(n);
  }
  const sortedKeys = Object.keys(groups).sort().reverse();

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <header className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-medium flex items-center gap-2">
            <Award size={22} className="text-notion-500" /> Catas
          </h1>
          <p className="text-sm text-notion-500">
            Historial de vinos probados. Incluye vinos de cava y vinos solo probados.
          </p>
        </div>
        <Link to="/tastings/photo" className="btn-primary">
          <Camera size={14} /> Probar vino
        </Link>
      </header>

      {isLoading ? (
        <p className="text-notion-500">Cargando…</p>
      ) : sortedKeys.length === 0 ? (
        <div className="card p-8 text-center text-notion-500">
          <Award size={32} className="mx-auto text-notion-300 mb-3" />
          <p>Todavía no registraste catas.</p>
          <p className="text-xs mt-1">
            Sacale una foto al próximo vino que pruebes y la IA lo carga por vos.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedKeys.map((k) => (
            <div key={k}>
              <p className="text-xs uppercase tracking-wide text-notion-500 mb-1">
                {monthLabel(k)} · {fmtInt(groups[k].length)} cata{groups[k].length === 1 ? '' : 's'}
              </p>
              <ul className="card divide-y divide-notion-100">
                {groups[k].map((n) => (
                  <li key={n.id}>
                    <Link
                      to={`/tastings/${n.id}/edit`}
                      className="block p-3 hover:bg-notion-50"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-notion-800 truncate">
                            {n.wine?.name ?? 'Vino sin nombre'}
                          </p>
                          <p className="text-xs text-notion-500 truncate">
                            {n.wine?.vintage ?? '—'} · {n.wine?.format ?? '750ml'} ·{' '}
                            {new Date(n.tasted_on).toLocaleDateString('es-AR', {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {n.score != null && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-notion-100 text-notion-800 tabular-nums">
                              {n.score}/{n.scale === 'propio' ? '★' : n.scale}
                            </span>
                          )}
                          <ChevronRight size={14} className="text-notion-400" />
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function monthLabel(yyyymm: string): string {
  const d = new Date(yyyymm + '-01T12:00:00');
  return d.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
}
